import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { 
  Save, Plus, Trash2, 
  Clock, Zap, AlertCircle,
  MapPin, Check, X,
  Edit3, Layers, Calendar
} from 'lucide-react';

import { API_URL as API } from '../../config/api';
import TimePicker12, { DAY_RANGE_OPTIONS } from './TimePicker12';

export default function PartnerPricing({ data, onTabChange }) {
  const { token } = useAuth();
  const [selectedTurf, setSelectedTurf] = useState(data?.turfs?.[0]?._id || null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const currentTurf = data?.turfs?.find(t => t._id === selectedTurf) || data?.turfs?.[0];

  // Editable prices — sync when turf changes
  const [basePrice, setBasePrice] = useState(currentTurf?.price_per_hour || 0);
  const [rules, setRules] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newRule, setNewRule] = useState({
    category: '',
    type: 'Weekday',
    customDayRange: '',
    startTime: '06:00 AM',
    endTime: '12:00 PM',
    price: ''
  });

  const handleTurfSelect = (id) => {
    setSelectedTurf(id);
    const turf = data?.turfs?.find(t => t._id === id);
    if (turf) setBasePrice(turf.price_per_hour || 0);
  };

  // Load rules when selectedTurf or basePrice changes
  useEffect(() => {
    if (currentTurf) {
      // Prefer backend-saved rules
      if (currentTurf.pricingRules && currentTurf.pricingRules.length > 0) {
        // Sync base price in the base rule
        const updated = currentTurf.pricingRules.map(r =>
          r.id === `${currentTurf._id}-base` ? { ...r, price: basePrice } : r
        );
        setRules(updated);
        return;
      }

      // Fall back to localStorage cache
      const savedRules = localStorage.getItem(`turf_pricing_rules_${currentTurf._id}`);
      if (savedRules) {
        try {
          const parsed = JSON.parse(savedRules);
          const updated = parsed.map(r => {
            if (r.id === `${currentTurf._id}-base`) {
              return { ...r, price: basePrice };
            }
            return r;
          });
          setRules(updated);
          return;
        } catch (e) {
          console.error(e);
        }
      }
      
      // Default fallback rules without prime time
      setRules([
        { id: `${currentTurf._id}-base`, type: 'Weekday', startTime: '06:00 AM', endTime: '06:00 PM', price: basePrice, category: 'Standard Rate', active: true },
        { id: `${currentTurf._id}-weekend`, type: 'Weekend', startTime: '06:00 AM', endTime: '11:00 PM', price: Math.round(basePrice * 1.2), category: 'Weekend Rate', active: true },
      ]);
    }
  }, [selectedTurf, basePrice]);

  const toggleRuleActive = (id) => {
    const updatedRules = rules.map(r => r.id === id ? { ...r, active: !r.active } : r);
    setRules(updatedRules);
    if (currentTurf) {
      localStorage.setItem(`turf_pricing_rules_${currentTurf._id}`, JSON.stringify(updatedRules));
    }
  };

  const deleteRule = (id) => {
    const updatedRules = rules.filter(r => r.id !== id);
    setRules(updatedRules);
    if (currentTurf) {
      localStorage.setItem(`turf_pricing_rules_${currentTurf._id}`, JSON.stringify(updatedRules));
    }
  };

  const handlePriceChange = (id, newPrice) => {
    const updatedRules = rules.map(r => r.id === id ? { ...r, price: parseFloat(newPrice) || 0 } : r);
    setRules(updatedRules);
    if (currentTurf) {
      localStorage.setItem(`turf_pricing_rules_${currentTurf._id}`, JSON.stringify(updatedRules));
    }
  };

  const handleAddRule = (e) => {
    e.preventDefault();
    if (!newRule.category || !newRule.price) {
      setError('Please fill in all fields for the new rule');
      return;
    }
    const finalDayType = newRule.type === 'Custom' && newRule.customDayRange ? newRule.customDayRange : newRule.type;
    const rule = {
      id: `${currentTurf?._id || 'custom'}-${Date.now()}`,
      category: newRule.category,
      type: finalDayType,
      startTime: newRule.startTime,
      endTime: newRule.endTime,
      price: parseFloat(newRule.price) || 0,
      active: true
    };
    const updatedRules = [...rules, rule];
    setRules(updatedRules);
    if (currentTurf) {
      localStorage.setItem(`turf_pricing_rules_${currentTurf._id}`, JSON.stringify(updatedRules));
    }
    setShowModal(false);
    setNewRule({
      category: '',
      type: 'Weekday',
      customDayRange: '',
      startTime: '06:00 AM',
      endTime: '12:00 PM',
      price: ''
    });
  };

  const handleSave = async () => {
    if (!currentTurf) return;
    setSaving(true);
    setError('');
    try {
      await axios.put(`${API}/turfs/${currentTurf._id}`, {
        price_per_hour: parseFloat(basePrice),
        pricingRules: rules,
      }, { headers: { Authorization: `Bearer ${token}` } });
      // Also persist locally as a cache
      localStorage.setItem(`turf_pricing_rules_${currentTurf._id}`, JSON.stringify(rules));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to save pricing');
    } finally {
      setSaving(false);
    }
  };

  // Compute price range across base price and active rules / slot schedules
  const allActivePrices = [
    ...(basePrice > 0 ? [parseFloat(basePrice)] : []),
    ...rules.filter(r => r.active && parseFloat(r.price) > 0).map(r => parseFloat(r.price)),
    ...(currentTurf?.timings?.flatMap(t => [
      ...(t.price && parseFloat(t.price) > 0 ? [parseFloat(t.price)] : []),
      ...(Array.isArray(t.slots) ? t.slots.filter(s => s.active !== false && parseFloat(s.price) > 0).map(s => parseFloat(s.price)) : [])
    ]) || [])
  ];

  const minPrice = allActivePrices.length > 0 ? Math.min(...allActivePrices) : (parseFloat(basePrice) || 0);
  const maxPrice = allActivePrices.length > 0 ? Math.max(...allActivePrices) : (parseFloat(basePrice) || 0);

  const handleEditVenue = () => {
    if (onTabChange) {
      onTabChange('venues');
    }
  };

  return (
    <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: '800', color: '#161616', marginBottom: '8px' }}>Pricing Management</h2>
          <p style={{ color: '#374151', fontWeight: '500' }}>Manage all price ranges, time slots, and schedules for your turf venues</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button 
            onClick={handleEditVenue}
            style={{
              background: '#F4F6F3',
              color: '#084734',
              border: '1.5px solid #DCEFB8',
              padding: '12px 20px',
              borderRadius: '12px',
              fontWeight: '800',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <Edit3 size={18} color="#084734" />
            Edit Venue & Slots
          </button>
          <button onClick={handleSave} disabled={saving} style={btnPrimary}>
            {saved ? <><Check size={18} /> Saved!</> : saving ? 'Saving...' : <><Save size={18} /> Save Changes</>}
          </button>
        </div>
        {error && <div style={{ width: '100%', fontSize: '0.85rem', color: '#ef4444', fontWeight: '700', marginTop: '4px' }}>{error}</div>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
        {/* LEFT: TURF SELECTION & INFO */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
           <h3 style={{ fontSize: '0.9rem', fontWeight: '800', color: '#161616', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Your Venues</h3>
           {data?.turfs?.map(t => (
             <div 
               key={t._id} 
               onClick={() => handleTurfSelect(t._id)}
               style={{ 
                 padding: '1.2rem', borderRadius: '16px', border: '1.5px solid #EEF2E6',
                 background: selectedTurf === t._id ? '#DCEFB8' : 'white',
                 borderColor: selectedTurf === t._id ? '#CEF17B' : '#EEF2E6',
                 cursor: 'pointer', transition: '0.2s'
               }}
             >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontWeight: '800', color: selectedTurf === t._id ? '#161616' : '#374151', fontSize: '0.95rem', marginBottom: '4px' }}>{t.name}</div>
                  {selectedTurf === t._id && (
                    <span style={{ fontSize: '0.72rem', background: '#CEF17B', color: '#084734', fontWeight: '800', padding: '2px 8px', borderRadius: '6px' }}>Active</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: '#374151' }}>
                   <MapPin size={12} /> {t.location}
                </div>
                <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.78rem', color: '#4b5563' }}>
                  <span>Base: <strong>Rs. {t.price_per_hour || 0}/hr</strong></span>
                  <span style={{ color: '#084734', fontWeight: '700', textDecoration: 'underline' }} onClick={(e) => { e.stopPropagation(); handleEditVenue(); }}>Edit Venue →</span>
                </div>
             </div>
           ))}
        </div>

        {/* RIGHT: PRICING RULES & RANGES */}
        <div>
           {/* QUICK STATS */}
           <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={priceStatCard}>
                 <div style={{ fontSize: '0.8rem', color: '#374151', fontWeight: '700', marginBottom: '8px' }}>Base Hourly Rate</div>
                 <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                   <span style={{ fontSize: '1.1rem', fontWeight: '800', color: '#161616' }}>Rs.</span>
                   <input 
                     type="number" 
                     value={basePrice} 
                     onChange={e => setBasePrice(parseFloat(e.target.value) || 0)}
                     style={{ fontSize: '1.5rem', fontWeight: '900', color: '#161616', border: 'none', outline: 'none', width: '100%', background: 'transparent' }}
                   />
                 </div>
              </div>
              <div style={priceStatCard}>
                 <div style={{ fontSize: '0.8rem', color: '#374151', fontWeight: '700', marginBottom: '8px' }}>Price Range</div>
                 <div style={{ fontSize: '1.35rem', fontWeight: '900', color: '#084734' }}>
                   {minPrice === maxPrice ? `Rs. ${minPrice}/hr` : `Rs. ${minPrice} - ${maxPrice}/hr`}
                 </div>
                 <div style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: '600', marginTop: '4px' }}>
                   Across all time slots & rules
                 </div>
              </div>
              <div style={priceStatCard}>
                 <div style={{ fontSize: '0.8rem', color: '#374151', fontWeight: '700', marginBottom: '8px' }}>Active Rules</div>
                 <div style={{ fontSize: '1.5rem', fontWeight: '900', color: '#3b82f6' }}>
                   {rules.filter(r => r.active).length}
                 </div>
                 <div style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: '600', marginTop: '4px' }}>
                   Slot price overrides
                 </div>
              </div>
           </div>

           {/* ALL PRICE RANGES BREAKDOWN */}
           {currentTurf?.timings && currentTurf.timings.length > 0 && (
             <div style={{ background: 'white', borderRadius: '20px', border: '1.5px solid #EEF2E6', padding: '1.2rem', marginBottom: '1.5rem' }}>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                 <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                   <Calendar size={18} color="#084734" />
                   <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '800', color: '#161616' }}>Venue Timing & Slot Schedules</h4>
                 </div>
                 <button 
                   onClick={handleEditVenue}
                   style={{ background: 'transparent', border: 'none', color: '#084734', fontWeight: '800', fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                 >
                   <Edit3 size={14} /> Edit Timings
                 </button>
               </div>
               <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.8rem' }}>
                 {currentTurf.timings.map((sched, idx) => (
                   <div key={sched.id || idx} style={{ background: '#F8FAF7', padding: '12px 14px', borderRadius: '12px', border: '1px solid #E9EDE8' }}>
                     <div style={{ fontWeight: '800', fontSize: '0.85rem', color: '#161616', marginBottom: '4px' }}>{sched.name || `Schedule ${idx + 1}`}</div>
                     <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: '#4b5563', marginBottom: '6px' }}>
                       <Clock size={12} /> {sched.open || '06:00 AM'} - {sched.close || '10:00 PM'}
                     </div>
                     <div style={{ fontSize: '0.85rem', fontWeight: '800', color: '#084734' }}>
                       Rate: Rs. {sched.price || basePrice || currentTurf.price_per_hour || 0} / hr
                     </div>
                   </div>
                 ))}
               </div>
             </div>
           )}

           {/* RULES LIST */}
           <div style={{ background: 'white', borderRadius: '24px', border: '1.5px solid #EEF2E6', overflow: 'hidden' }}>
              <div style={{ padding: '1.5rem', borderBottom: '1.5px solid #EEF2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                 <div>
                   <h3 style={{ fontSize: '1.1rem', fontWeight: '800', margin: 0, color: '#161616' }}>All Price Ranges & Rules</h3>
                   <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#6b7280' }}>Configured pricing rules and rates for active time slots</p>
                 </div>
                 <div style={{ display: 'flex', gap: '8px' }}>
                   <button onClick={() => setShowModal(true)} style={btnSmall}><Plus size={16} /> Add Price Range</button>
                 </div>
              </div>

               <div style={{ padding: '1.5rem', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                     <thead>
                        <tr style={{ textAlign: 'left' }}>
                           <th style={thStyle}>Price Range / Name</th>
                           <th style={thStyle}>Day Range</th>
                           <th style={thStyle}>Time Slot</th>
                           <th style={thStyle}>Amount (Rs./Hr)</th>
                           <th style={thStyle}>Status</th>
                           <th style={thStyle}></th>
                        </tr>
                     </thead>
                     <tbody>
                        {rules.map((rule) => (
                          <tr key={rule.id} style={{ borderBottom: '1px solid #EEF2E6', opacity: rule.active ? 1 : 0.6 }}>
                             <td style={tdStyle}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                   <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#F8FAF7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <Zap size={16} color={rule.active ? "#084734" : "#94a3b8"} />
                                   </div>
                                   <div style={{ fontWeight: '700', fontSize: '0.9rem', color: rule.active ? '#161616' : '#374151' }}>{rule.category}</div>
                                </div>
                             </td>
                             <td style={tdStyle}>
                                <span style={{ padding: '4px 10px', borderRadius: '6px', background: '#EEF2E6', fontSize: '0.75rem', fontWeight: '700', color: '#374151' }}>{rule.type}</span>
                             </td>
                             <td style={tdStyle}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#374151', fontSize: '0.85rem', fontWeight: '600' }}>
                                   <Clock size={14} /> {rule.startTime} - {rule.endTime}
                                </div>
                             </td>
                             <td style={tdStyle}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                   <input 
                                     type="number" 
                                     value={rule.price} 
                                     onChange={e => handlePriceChange(rule.id, e.target.value)}
                                     style={{ width: '80px', padding: '6px 10px', borderRadius: '8px', border: '1.5px solid #EEF2E6', fontWeight: '800', outline: 'none', textAlign: 'center' }}
                                   />
                                </div>
                             </td>
                             <td style={tdStyle}>
                                <div 
                                  onClick={() => toggleRuleActive(rule.id)}
                                  style={{ 
                                    width: '40px', height: '22px', 
                                    background: rule.active ? '#CEF17B' : '#cbd5e1', 
                                    borderRadius: '20px', position: 'relative', cursor: 'pointer',
                                    transition: '0.3s'
                                  }}
                                >
                                   <div style={{ 
                                     position: 'absolute', 
                                     left: rule.active ? '20px' : '2px', 
                                     top: '2px', width: '18px', height: '18px', 
                                     borderRadius: '50%', background: 'white',
                                     transition: '0.3s'
                                   }}></div>
                                </div>
                             </td>
                             <td style={tdStyle}>
                                <button 
                                  onClick={() => deleteRule(rule.id)}
                                  style={{ border: 'none', background: 'none', color: '#374151', cursor: 'pointer', transition: '0.2s' }}
                                  onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                                  onMouseLeave={e => e.currentTarget.style.color = '#374151'}
                                >
                                  <Trash2 size={18} />
                                </button>
                             </td>
                          </tr>
                        ))}
                     </tbody>
                  </table>
               </div>
               
               <div style={{ padding: '1.5rem', background: '#F8FAF7', display: 'flex', alignItems: 'center', gap: '12px', borderTop: '1.5px solid #EEF2E6' }}>
                  <AlertCircle size={18} color="#084734" />
                  <p style={{ fontSize: '0.85rem', color: '#374151', fontWeight: '500' }}>
                     Tip: You can set custom price ranges for different day ranges and time slots, or click "Edit Venue & Slots" to customize individual court slot schedules.
                  </p>
               </div>
            </div>
         </div>
      </div>

      {/* ADD NEW RULE MODAL */}
      {showModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#161616', margin: 0 }}>Add Pricing Rule</h3>
              <button onClick={() => setShowModal(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddRule} style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
              <div>
                <label style={labelStyle}>Rule Category / Name</label>
                <input 
                  type="text" 
                  placeholder="e.g. Night Owl, Early Bird"
                  value={newRule.category}
                  onChange={e => setNewRule({...newRule, category: e.target.value})}
                  style={inputStyle}
                  required
                />
              </div>
              
              <div>
                <label style={labelStyle}>Day Range / Applicability</label>
                <select 
                  value={newRule.type}
                  onChange={e => setNewRule({...newRule, type: e.target.value})}
                  style={selectStyle}
                >
                  {DAY_RANGE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                  <option value="Custom">Custom Day Range...</option>
                </select>
                {newRule.type === 'Custom' && (
                  <input
                    type="text"
                    placeholder="e.g. Monday - Wednesday"
                    value={newRule.customDayRange}
                    onChange={e => setNewRule({...newRule, customDayRange: e.target.value})}
                    style={{ ...inputStyle, marginTop: '8px' }}
                    required
                  />
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <TimePicker12
                    label="Start Time"
                    value={newRule.startTime}
                    onChange={val => setNewRule({...newRule, startTime: val})}
                  />
                </div>
                <div>
                  <TimePicker12
                    label="End Time"
                    value={newRule.endTime}
                    onChange={val => setNewRule({...newRule, endTime: val})}
                  />
                </div>
              </div>

              <div>
                <label style={labelStyle}>Price / Hour (Rs.)</label>
                <input 
                  type="number" 
                  placeholder="e.g. 1200"
                  value={newRule.price}
                  onChange={e => setNewRule({...newRule, price: e.target.value})}
                  style={inputStyle}
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.2rem' }}>
                <button type="button" onClick={() => setShowModal(false)} style={btnSecondary}>Cancel</button>
                <button type="submit" style={btnSubmitRule}>Add Rule</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const priceStatCard = {
  background: 'white', padding: '1.5rem', borderRadius: '20px', border: '1.5px solid #EEF2E6'
};

const btnPrimary = {
  background: '#CEF17B', color: '#084734', border: 'none', padding: '12px 24px',
  borderRadius: '12px', fontWeight: '800', fontSize: '0.9rem', display: 'flex',
  alignItems: 'center', gap: '8px', cursor: 'pointer', boxShadow: '0 8px 20px rgba(30,190,116,0.2)'
};

const btnSmall = {
  background: '#DCEFB8', color: '#084734', border: 'none', padding: '8px 16px',
  borderRadius: '10px', fontWeight: '800', fontSize: '0.8rem', display: 'flex',
  alignItems: 'center', gap: '6px', cursor: 'pointer'
};

const thStyle = { textAlign: 'left', padding: '1rem', fontSize: '0.75rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase' };
const tdStyle = { padding: '1.2rem', verticalAlign: 'middle' };

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(15, 23, 42, 0.4)',
  WebkitBackdropFilter: 'blur(8px)',
  backdropFilter: 'blur(8px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalContentStyle = {
  background: 'white',
  padding: '2rem',
  borderRadius: '24px',
  width: '100%',
  maxWidth: '520px',
  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: '800',
  color: '#374151',
  textTransform: 'uppercase',
  marginBottom: '6px'
};

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: '10px',
  border: '1.5px solid #DCEFB8',
  outline: 'none',
  fontSize: '0.9rem',
  fontWeight: '700',
  boxSizing: 'border-box'
};

const selectStyle = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: '10px',
  border: '1.5px solid #DCEFB8',
  outline: 'none',
  fontSize: '0.9rem',
  fontWeight: '700',
  background: 'white',
  boxSizing: 'border-box'
};

const btnSecondary = {
  flex: 1,
  background: '#EEF2E6',
  color: '#374151',
  border: 'none',
  padding: '12px',
  borderRadius: '12px',
  fontWeight: '800',
  cursor: 'pointer',
  textAlign: 'center'
};

const btnSubmitRule = {
  flex: 1,
  background: '#CEF17B',
  color: 'white',
  border: 'none',
  padding: '12px',
  borderRadius: '12px',
  fontWeight: '800',
  cursor: 'pointer',
  textAlign: 'center',
  boxShadow: '0 4px 12px rgba(30,190,116,0.2)'
};
